import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Printer, Download, X, Loader2 } from 'lucide-react';
import html2pdf from 'html2pdf.js';

/**
 * Invoice preview modal with on-screen render, Print and Download PDF.
 *
 * Pulls full line-item breakdown from the parent contract (food, bar, hors,
 * late night, rentals, custom add-ons, custom menu tiers) so the invoice
 * matches what was sold — not just a single bucket total.
 */
const PAYMENT_FOOTER = {
  business: 'Willow Ridge Golf and Country Club — Catering & Events',
  address: '439 Chatham Street South, Blenheim, ON N0P 1A0',
  phone: '519-676-8772',
  email: 'admin@willowridgegolf.ca',
  methods: [
    'E-Transfer: admin@willowridgegolf.ca',
    'Cheque payable to: Willow Ridge Golf and Country Club',
  ],
  terms: 'Final balance due 7 days before event date • HST included where applicable',
};

const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

/** Parse YYYY-MM-DD as a LOCAL date so it doesn't roll back a day in negative-UTC zones. */
const parseLocalDate = (iso) => {
  if (!iso) return null;
  // Strings like "2026-06-15" → treat as local midnight, not UTC
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (m) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};
const fmtDateLong = (iso) => {
  const d = parseLocalDate(iso);
  if (!d) return '';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};
const fmtDateShort = (iso) => {
  const d = parseLocalDate(iso);
  if (!d) return '';
  return d.toLocaleDateString();
};

/** Flatten everything sold on the contract into invoice line items. */
const buildInvoiceLines = (contract) => {
  if (!contract) return [];
  const lines = [];
  const guests = parseInt(contract.guest_count) || 0;
  const num = (v) => (parseFloat(v) || 0);
  const int = (v) => (parseInt(v) || 0);

  // Food menus (paid per person × guests)
  (contract.selected_menus_details || []).forEach((m) => {
    const pp = num(m.price_per_person);
    if (pp > 0) lines.push({ desc: m.name || 'Menu', qty: guests, unit: pp, total: pp * guests });
  });

  // Buffet
  (contract.selected_buffet || []).forEach((b) => {
    const pp = num(b.price_per_person);
    const flat = num(b.price);
    if (pp > 0) lines.push({ desc: `${b.name || 'Buffet item'} (Buffet)`, qty: guests, unit: pp, total: pp * guests });
    else if (flat > 0) {
      const q = int(b.quantity) || 1;
      lines.push({ desc: `${b.name || 'Buffet item'} (Buffet)`, qty: q, unit: flat, total: q * flat });
    }
  });

  // Hors d'oeuvres
  (contract.selected_hors_doeuvres || []).forEach((h) => {
    const pp = num(h.price_per_person);
    if (pp > 0) lines.push({ desc: `${h.name} (Hors d'Oeuvre)`, qty: guests, unit: pp, total: pp * guests });
  });

  // Late night
  (contract.selected_late_night || []).forEach((ln) => {
    const pp = num(ln.price_per_person);
    if (pp > 0) lines.push({ desc: `${ln.name} (Late Night)`, qty: guests, unit: pp, total: pp * guests });
  });

  // Bar package (flat or per-person)
  (contract.selected_bar || []).forEach((b) => {
    const flat = num(b.price);
    const pp = num(b.price_per_person);
    if (pp > 0) lines.push({ desc: `${b.name} (Bar)`, qty: guests, unit: pp, total: pp * guests });
    else if (flat > 0) lines.push({ desc: `${b.name} (Bar)`, qty: 1, unit: flat, total: flat });
  });

  // Rentals
  (contract.selected_rentals || []).forEach((r) => {
    const qty = num(r.quantity) || 1;
    const price = num(r.price);
    if (price > 0) lines.push({ desc: `${r.name} (Rental)`, qty, unit: price, total: qty * price });
  });

  // Plated courses — appetizer, salad, mains[], dessert
  const platedCourse = (course, label) => {
    if (!course) return;
    const pp = num(course.price_per_person);
    if (pp > 0) lines.push({ desc: `${course.name} (Plated ${label})`, qty: guests, unit: pp, total: pp * guests });
  };
  platedCourse(contract.selected_plated_appetizer, 'Appetizer');
  platedCourse(contract.selected_plated_salad, 'Salad');
  (contract.selected_plated_mains || []).forEach((pm) => {
    const pp = num(pm.price_per_person);
    const qty = int(pm.guest_count) || guests;
    if (pp > 0) lines.push({ desc: `${pm.name} (Plated Main)`, qty, unit: pp, total: pp * qty });
  });
  platedCourse(contract.selected_plated_dessert, 'Dessert');

  // On the Turn — stored as {name, price_per_person, quantity} where quantity = number of portions/guests
  (contract.selected_on_the_turn || []).forEach((ot) => {
    const q = int(ot.quantity) || 1;
    const pp = num(ot.price_per_person);
    const flat = num(ot.price);
    if (pp > 0) {
      lines.push({ desc: `${ot.name} (On the Turn)`, qty: q, unit: pp, total: q * pp });
    } else if (flat > 0) {
      lines.push({ desc: `${ot.name} (On the Turn)`, qty: q, unit: flat, total: q * flat });
    }
  });

  // Selected add-ons (catalog) — flat × qty or per-person
  (contract.selected_addons || []).forEach((a) => {
    const q = num(a.quantity) || 1;
    const p = num(a.price) || num(a.rate);
    if (p <= 0) return;
    if (a.is_per_person) {
      lines.push({ desc: `${a.name || 'Add-on'} (Per Person)`, qty: guests, unit: p, total: p * guests });
    } else {
      lines.push({ desc: a.name || 'Add-on', qty: q, unit: p, total: q * p });
    }
  });

  // Custom add-ons (Staff & Custom Charges) — backend shape is one of:
  //   { name, pricing_mode: 'hourly', hours, rate, total }
  //   { name, pricing_mode: 'per_person', rate, guest_count_at_add, total }
  //   { name, pricing_mode: 'flat', rate, total }
  //   { name, hours, rate, total }   ← legacy hourly without pricing_mode
  //   { name, quantity, price }      ← very old catalog shape
  (contract.custom_addons || []).forEach((a) => {
    const total = num(a.total);
    const hours = num(a.hours);
    const rate = num(a.rate);
    const qty = num(a.quantity);
    const price = num(a.price);
    const ppl = int(a.guest_count_at_add) || guests;
    const mode = a.pricing_mode;
    const baseName = a.name || 'Add-on';

    let desc = baseName;
    let lineQty = 1;
    let lineUnit = 0;
    let lineTotal = 0;

    if (mode === 'hourly' || (hours > 0 && rate > 0)) {
      lineQty = hours || 1;
      lineUnit = rate;
      lineTotal = total > 0 ? total : (lineQty * lineUnit);
      desc = `${baseName} (${lineQty} hrs)`;
    } else if (mode === 'per_person' || a.is_per_person) {
      lineQty = ppl;
      lineUnit = rate > 0 ? rate : price;
      lineTotal = total > 0 ? total : (lineQty * lineUnit);
      desc = `${baseName} (Per Person)`;
    } else if (mode === 'per_item') {
      lineQty = int(a.quantity) || 1;
      lineUnit = rate > 0 ? rate : price;
      lineTotal = total > 0 ? total : (lineQty * lineUnit);
      // Description is self-explanatory with qty + unit price columns
    } else if (mode === 'flat') {
      lineQty = 1;
      lineUnit = rate > 0 ? rate : total;
      lineTotal = total > 0 ? total : lineUnit;
    } else if (qty > 0 && price > 0) {
      lineQty = qty;
      lineUnit = price;
      lineTotal = qty * price;
    } else if (total > 0) {
      lineQty = 1;
      lineUnit = total;
      lineTotal = total;
    } else {
      return; // no usable pricing data
    }

    if (lineTotal <= 0) return;
    lines.push({ desc, qty: lineQty, unit: lineUnit, total: lineTotal });
  });

  // Custom menu tiers per phase (Breakfast, Lunch, Cocktail, Dinner, Dessert, Late Night, Bar, Beverage, On the Turn)
  const phases = [
    ['Breakfast', contract.custom_menu_breakfast_tiers, contract.custom_menu_breakfast_price],
    ['Lunch', contract.custom_menu_lunch_tiers, contract.custom_menu_lunch_price],
    ['Cocktail Hour', contract.custom_menu_cocktail_tiers, contract.custom_menu_cocktail_price],
    ['Dinner', contract.custom_menu_dinner_tiers, contract.custom_menu_dinner_price],
    ['Dessert', contract.custom_menu_dessert_tiers, contract.custom_menu_dessert_price],
    ['Late Night', contract.custom_menu_late_night_tiers, contract.custom_menu_late_night_price],
    ['Bar', contract.custom_menu_bar_tiers, contract.custom_menu_bar_price],
    ['Beverage', contract.custom_menu_beverage_tiers, contract.custom_menu_beverage_price],
    ['On the Turn', contract.custom_menu_on_the_turn_tiers, contract.custom_menu_on_the_turn_price],
  ];
  phases.forEach(([label, tiers, fallbackPP]) => {
    const validTiers = (tiers || []).filter(
      (t) => int(t.quantity) > 0 && num(t.price_per_person) > 0,
    );
    if (validTiers.length > 0) {
      validTiers.forEach((t) => {
        const qty = int(t.quantity);
        const pp = num(t.price_per_person);
        lines.push({ desc: `${label}: ${t.label || 'Item'}`, qty, unit: pp, total: qty * pp });
      });
    } else if (num(fallbackPP) > 0 && guests > 0) {
      lines.push({ desc: `${label} (Custom Menu)`, qty: guests, unit: num(fallbackPP), total: num(fallbackPP) * guests });
    }
  });

  // Travel / Mileage
  if (num(contract.travel_fee) > 0) {
    lines.push({ desc: 'Travel / Mileage', qty: 1, unit: num(contract.travel_fee), total: num(contract.travel_fee) });
  }

  // Discount (negative line)
  if (num(contract.discount_total) > 0) {
    lines.push({ desc: 'Discount', qty: 1, unit: -num(contract.discount_total), total: -num(contract.discount_total) });
  }

  return lines;
};

export const InvoicePreviewModal = ({ open, onClose, invoice, contract }) => {
  const printRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!invoice) return null;

  const lines = buildInvoiceLines(contract);
  const computedSubtotal = lines.reduce((s, l) => s + l.total, 0);
  // Prefer the live line-item subtotal so the invoice reconciles with what's listed.
  // Fall back to the stored invoice subtotal only if the contract has no itemizable data.
  const subtotal = computedSubtotal > 0 ? computedSubtotal : (parseFloat(invoice.subtotal) || 0);
  // Tax: prefer stored value > 0, else recompute at 13% on subtotal
  const storedTax = parseFloat(invoice.tax_amount) || 0;
  const tax = storedTax > 0 ? storedTax : +(subtotal * 0.13).toFixed(2);
  const gratuity = parseFloat(invoice.gratuity_amount) || 0;
  // Total: stored if positive, else recomputed
  const storedTotal = parseFloat(invoice.total_amount) || 0;
  const total = storedTotal > 0 ? storedTotal : +(subtotal + tax + gratuity).toFixed(2);
  // Payments made against this invoice.  For legacy invoices without a
  // `payments` array we synthesise entries from deposit_paid / balance_paid
  // so the invoice still renders a sensible history.
  const paymentsList = Array.isArray(invoice.payments) && invoice.payments.length > 0
    ? invoice.payments
    : ([
        invoice.deposit_paid ? {
          id: 'legacy-deposit',
          amount: invoice.deposit_amount || 0,
          date: invoice.deposit_paid_date,
          method: 'Deposit',
          note: 'Legacy deposit'
        } : null,
        invoice.balance_paid ? {
          id: 'legacy-balance',
          amount: invoice.balance_due || 0,
          date: invoice.balance_paid_date,
          method: 'Balance',
          note: 'Legacy balance payment'
        } : null,
      ].filter(Boolean));
  const amountPaid = +(paymentsList.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)).toFixed(2);
  const balance = +Math.max(total - amountPaid, 0).toFixed(2);

  const handlePrint = () => {
    // Radix Dialogs use portals + transforms which can mangle @media print rules
    // across browsers. Open the printable invoice HTML in a fresh window and print
    // from there — reliable in Chrome / Safari / Firefox / Edge.
    const el = printRef.current;
    if (!el) return;
    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) {
      alert('Pop-up blocked. Please allow pop-ups for this site to print invoices.');
      return;
    }
    // Pull computed styles by cloning stylesheets from the parent doc.
    const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map((l) => l.outerHTML).join('\n');
    const styleTags = Array.from(document.querySelectorAll('style'))
      .map((s) => `<style>${s.innerHTML}</style>`).join('\n');
    w.document.open();
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${invoice.invoice_number || ''}</title>${styleLinks}${styleTags}
      <style>
        @page { size: letter; margin: 0.4in; }
        body { background: #fff; margin: 0; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
        .print-content { box-shadow: none !important; }
      </style></head><body>${el.outerHTML}</body></html>`);
    w.document.close();
    // Wait for styles to load before printing.
    w.onload = () => {
      w.focus();
      setTimeout(() => { w.print(); w.close(); }, 200);
    };
  };

  const handleDownloadPDF = async () => {
    const element = printRef.current;
    if (!element) return;
    setBusy(true);
    try {
      await html2pdf().set({
        margin: [0.4, 0.4, 0.4, 0.4],
        filename: `Invoice_${invoice.invoice_number}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, windowWidth: element.scrollWidth },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      }).from(element).save();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="invoice-preview-modal">
        <DialogTitle className="sr-only">Invoice Preview {invoice.invoice_number}</DialogTitle>
        <DialogDescription className="sr-only">Preview, print, or download the invoice.</DialogDescription>

        {/* Toolbar (hidden in print) */}
        <div className="sticky top-0 z-10 bg-white border-b px-4 py-2 flex items-center justify-between no-print">
          <span className="text-sm font-semibold text-[#1e3a5f]">Invoice Preview · {invoice.invoice_number}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="invoice-print-btn">
              <Printer size={14} className="mr-1" />Print
            </Button>
            <Button
              size="sm"
              className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
              onClick={handleDownloadPDF}
              disabled={busy}
              data-testid="invoice-download-pdf-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Download size={14} className="mr-1" />}
              {busy ? 'Building PDF…' : 'Download PDF'}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="invoice-preview-close">
              <X size={14} />
            </Button>
          </div>
        </div>

        {/* Printable invoice document */}
        <div ref={printRef} className="bg-white p-8 print-content" style={{ width: '100%', maxWidth: '8.5in', margin: '0 auto' }} data-testid="invoice-printable">
          <div className="border-b-2 border-[#1e3a5f] pb-4 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-[#1e3a5f] tracking-tight">WILLOW RIDGE</h1>
                <p className="text-sm text-[#8b7355] tracking-widest">CATERING & EVENTS</p>
                <p className="text-xs text-gray-500 mt-1">{PAYMENT_FOOTER.address}</p>
                <p className="text-xs text-gray-500">{PAYMENT_FOOTER.phone} · {PAYMENT_FOOTER.email}</p>
              </div>
              <div className="text-right">
                <h2 className="text-2xl font-bold text-[#1e3a5f]">INVOICE</h2>
                <p className="text-sm text-gray-700 mt-1">#{invoice.invoice_number}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Issued: {invoice.created_at ? fmtDateShort(invoice.created_at) : new Date().toLocaleDateString()}
                </p>
                {invoice.due_date && (
                  <p className="text-xs text-gray-500">Due: {fmtDateShort(invoice.due_date)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Bill To + Event Details */}
          <div className="grid grid-cols-2 gap-8 mb-6">
            <div>
              <h3 className="text-xs font-bold text-[#8b7355] uppercase tracking-wider mb-1">Bill To</h3>
              <p className="font-semibold text-[#1e3a5f]">{invoice.client_name}</p>
              {invoice.client_email && <p className="text-sm text-gray-600">{invoice.client_email}</p>}
              {invoice.client_phone && <p className="text-sm text-gray-600">{invoice.client_phone}</p>}
            </div>
            <div>
              <h3 className="text-xs font-bold text-[#8b7355] uppercase tracking-wider mb-1">Event</h3>
              <p className="font-semibold text-[#1e3a5f]">{invoice.event_name}</p>
              {invoice.event_date && (
                <p className="text-sm text-gray-600">
                  Date: {fmtDateLong(invoice.event_date)}
                </p>
              )}
              {contract?.guest_count && <p className="text-sm text-gray-600">Guests: {contract.guest_count}</p>}
              {contract?.location && <p className="text-sm text-gray-600">Location: {contract.location}</p>}
            </div>
          </div>

          {/* Line items */}
          <table className="w-full border-collapse mb-6 text-sm">
            <thead>
              <tr className="border-b-2 border-[#1e3a5f] bg-gray-50">
                <th className="text-left py-2 px-3 text-[#1e3a5f] font-bold">Description</th>
                <th className="text-right py-2 px-3 text-[#1e3a5f] font-bold w-20">Qty</th>
                <th className="text-right py-2 px-3 text-[#1e3a5f] font-bold w-28">Unit Price</th>
                <th className="text-right py-2 px-3 text-[#1e3a5f] font-bold w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={4} className="py-4 px-3 text-center text-gray-500 italic">No line items on source contract.</td></tr>
              ) : (
                lines.map((l, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 px-3 text-gray-800">{l.desc}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{l.qty}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{fmt(l.unit)}</td>
                    <td className="py-2 px-3 text-right text-gray-800 font-medium">{fmt(l.total)}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#1e3a5f]">
                <td colSpan={3} className="py-2 px-3 text-right font-semibold text-gray-700">Subtotal</td>
                <td className="py-2 px-3 text-right font-semibold text-gray-800">{fmt(subtotal)}</td>
              </tr>
              {tax > 0 && (
                <tr>
                  <td colSpan={3} className="py-1 px-3 text-right text-gray-600">HST (13%)</td>
                  <td className="py-1 px-3 text-right text-gray-700">{fmt(tax)}</td>
                </tr>
              )}
              {gratuity > 0 && (
                <tr>
                  <td colSpan={3} className="py-1 px-3 text-right text-gray-600">Gratuity</td>
                  <td className="py-1 px-3 text-right text-gray-700">{fmt(gratuity)}</td>
                </tr>
              )}
              <tr className="bg-[#1e3a5f] text-white">
                <td colSpan={3} className="py-2 px-3 text-right font-bold">TOTAL</td>
                <td className="py-2 px-3 text-right font-bold text-lg">{fmt(total)}</td>
              </tr>
            </tfoot>
          </table>

          {/* Payments Made + Balance */}
          <div className="mb-6 border-2 border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-[#1e3a5f] text-white px-3 py-2 text-xs font-semibold uppercase tracking-wide">
              Payments Received
            </div>
            {paymentsList.length === 0 ? (
              <p className="p-3 text-sm text-gray-500 italic">No payments recorded yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-600 text-xs uppercase bg-gray-50 border-b">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Method</th>
                    <th className="text-left px-3 py-2">Note</th>
                    <th className="text-right px-3 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentsList.map((p) => {
                    const d = parseLocalDate(p.date);
                    return (
                      <tr key={p.id || `${p.date}-${p.amount}`} className="border-b border-gray-100">
                        <td className="px-3 py-2">{d ? d.toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{p.method || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{p.note || ''}</td>
                        <td className="px-3 py-2 text-right font-semibold text-green-700">{fmt(p.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <div className="bg-gray-50 border-t-2 border-gray-300 px-3 py-2 flex items-center justify-between">
              <div className="text-sm">
                <span className="text-gray-600">Total Paid</span>{' '}
                <span className="font-bold text-green-700">{fmt(amountPaid)}</span>
              </div>
              <div className="text-sm">
                <span className="text-gray-600">Balance Owing</span>{' '}
                <span className="font-bold text-[#1e3a5f] text-lg">{fmt(balance)}</span>
              </div>
            </div>
          </div>

          {/* Payment instructions footer */}
          <div className="border-t-2 border-[#c9a227] pt-4 mt-6 text-sm">
            <h4 className="text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-2">Payment Instructions</h4>
            <ul className="text-xs text-gray-700 space-y-0.5 list-disc list-inside">
              {PAYMENT_FOOTER.methods.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 italic mt-2">{PAYMENT_FOOTER.terms}</p>
            <p className="text-center text-xs text-gray-400 mt-4">
              {PAYMENT_FOOTER.business} · Thank you for choosing Willow Ridge!
            </p>
          </div>
        </div>

        {/* Hidden print stylesheet kept inline for the popup window's HTML clone */}
        <style>{`
          @media print {
            .no-print { display: none !important; }
            .print-content { box-shadow: none !important; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePreviewModal;
